import React, {useContext, useEffect, useState, useCallback, useMemo} from 'react';
import { observer } from "mobx-react-lite";
import "../../styles/team.pc.css";
import "../../styles/table.pc.css";
import "./TagSelector.css"
import Search from "../../UiLib/Search/Search"
import '../../../globalStyles/dataTableStyles.css'
import ColorPicker from "../ColorPicker";
import BtnAdd from "../../buttons/btnAdd.component/BtnAdd";
import ExitCross from "../../../assets/cancelGreey.svg";
import {Context} from "../../../index";
import { AppContext } from '../../../App';
import axios from 'axios';
import { BASE_ITEM_COLOR } from '../../../constants/TagColorConst';
import { ITag } from '../../../common/api_interfaces/tag/ITag';
import { ICreateTegTask } from '../../../common/api_interfaces/tag/ICreateTegTask';

import { ReactComponent as AddTeg } from "../../../assets/images/addChekMark.svg"
import Button from '../../UiLib/Button/Button';
interface ITagSelectorProps {
    tags: ITag[];
    projectId: number;
    taskId: number | undefined;
    addTagsFunc: (tags: ITag[]) => Promise<void>;
    removeTagsFunc: (tags: ITag | ITag[]) => Promise<void>;
    closeFunc?: () => void;
}

const TAG_NAME_LIMIT = 15;

const TagSelector: React.FC<ITagSelectorProps> = ({
    tags, 
    projectId, 
    addTagsFunc, 
    removeTagsFunc,
    taskId
}) => {
    const [allTags, setAllTags] = useState<ITag[]>([]);
    const [filteredTags, setFilteredTags] = useState<ITag[]>([]);
    const [selectedTags, setSelectedTags] = useState<number[]>(tags.map(xx => xx.id));
    const [showCreatePopup, setShowCreatePopup] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [tagName, setTagName] = useState("");
    const [color, setColor] = useState<string | null>(null);
    
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);

    // Мемоизированные значения
    const canCreateTag = useMemo(() => tagName && color, [tagName, color]);

    // Получение всех тегов
    const getAllTags = useCallback(async () => {
        try {
            const response = await store.taskService.getTegs({
                taskId: taskId,
                projectId: projectId,
                userId: 0,
            });
            
            const tagsData = response.data.map((tag: ITag) => ({
                id: tag.id,
                name: tag.name,
                color: tag.color,
            }));
            
            setAllTags(tagsData);
            setFilteredTags(tagsData);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [projectId, store.taskService, showToast]);

    // Добавление нового тега
    const addNewTag = useCallback(async () => {
        try {
            const body: ICreateTegTask = {
                projectId: projectId,
                name: tagName,
                color: color ?? undefined,
            };
            await store.taskService.addTeg(body);
        } catch (error) {
            console.log(error)
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            } 
        }
    }, [projectId, tagName, color, store.taskService, showToast]);

    // Выбор тега
    const selectTag = useCallback(async (id: number) => {
        const isSelected = selectedTags.includes(id);

        let newSelectedTags: number[];

        if (!isSelected) {
            newSelectedTags = [...selectedTags, id];
            const tagsToAdd = allTags.filter(tag => newSelectedTags.includes(tag.id));
            await addTagsFunc(tagsToAdd);
        } else {
            newSelectedTags = selectedTags.filter(xx => xx !== id);
            const tagsToRemove = allTags.filter(tag => tag.id === id);
            await removeTagsFunc(tagsToRemove);
        }
        setSelectedTags(newSelectedTags);
    }, [selectedTags, allTags, addTagsFunc, removeTagsFunc]);

    // Обработчик выбора цвета
    const handleColorSelection = useCallback((selectedColor: string) => {
        setColor(selectedColor);
    }, []);

    // Обработчик поиска
    const handleChangeSearchText = useCallback((searchText: string) => {
        setSearchText(searchText);
        const searchTextLowercase = searchText.toLowerCase();
        const filtered = allTags.filter(t => t.name.toLowerCase().includes(searchTextLowercase));
        setFilteredTags(filtered);
    }, [allTags]);

    // Создание нового тега
    const onCreateNewTagAccept = useCallback(async () => {
        try {
            await addNewTag();
            await getAllTags();
            setShowCreatePopup(false);
            setTagName("");
            setColor(null);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            } else {
                showToast(`Не удалось создать тег: ${error}`);
            }
        }
    }, [addNewTag, getAllTags]);

    // Обработчик изменения имени тега
    const onTagNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setTagName(e.target.value);
    }, []);

    // Эффект для первоначальной загрузки тегов
    useEffect(() => {
        getAllTags();
    }, [getAllTags]);

    // Эффект для синхронизации выбранных тегов при изменении пропсов
    useEffect(() => {
        setSelectedTags(tags.map(tag => tag.id));
    }, [tags]);

    // Рендер заголовка в зависимости от режима
    const renderHeader = useMemo(() => {
        if (showCreatePopup) {
            return (
                <div className="add__teg--elem__header">
                    <button 
                        type="button" 
                        className='add__teg--elem__header--button-base' 
                        onClick={() => setShowCreatePopup(false)}
                    >
                        <img src={ExitCross} alt="Закрыть" />
                    </button>
                    <div className='widgets_header'>
                        <div className='task__teg-popup__add--teg'>
                            <input 
                                type="text" 
                                id="new-teg-name-input" 
                                name="new-teg-name-input" 
                                placeholder="Введите название для тега" 
                                maxLength={TAG_NAME_LIMIT}
                                value={tagName}
                                onChange={onTagNameChange}
                            />
                        </div>
                    </div>
                    {canCreateTag && (
                        <div className="add__teg--elem__header--add__teg">
                            <Button
                                id='button-cross'
                                image={<AddTeg/>}
                                callBack={onCreateNewTagAccept}
                            />
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="add__teg--elem__header">
                <BtnAdd 
                    type="add__teg--elem" 
                    onClickFunc={() => setShowCreatePopup(true)} 
                    permission={true}
                />
                <div className='widgets_header'>
                    <Search 
                        onSubmitFunc={undefined} 
                        value={searchText} 
                        changeSearchValue={handleChangeSearchText}
                    />
                </div>
            </div>
        );
    }, [showCreatePopup, tagName, canCreateTag, searchText, onTagNameChange, onCreateNewTagAccept, handleChangeSearchText]);

    // Рендер панели выбора цвета
    const renderColorPicker = useMemo(() => {
        if (!showCreatePopup) return null;

        return (
            <div style={{padding: "5px 10px"}}>
                <div>Цвет тега</div>
                <ColorPicker 
                    onColorSelected={handleColorSelection} 
                    options={BASE_ITEM_COLOR}
                />
            </div>
        );
    }, [showCreatePopup, handleColorSelection]);

    // Рендер списка тегов
    const renderTagsList = useMemo(() => {
        return (
            <div className='all__tegs--block' style={{overflowY: "auto"}}>
                {filteredTags.map(item => (
                    <div key={item.id} className='tag-selection-item'>
                        <div className='selected__tags--popup'>
                            <input 
                                type="checkbox" 
                                checked={selectedTags.includes(item.id)}
                                onChange={() => selectTag(item.id)}
                            />
                        </div>
                        <div 
                            className='selected__tags--popup__name' 
                            style={{backgroundColor: item.color}}
                        >
                            {item.name}
                        </div>
                    </div>
                ))}
            </div>
        );
    }, [showCreatePopup, filteredTags, selectedTags, selectTag]);

    return (
        <div style={{overflowY: "auto"}}>
            {renderHeader}
            {renderColorPicker}
            {renderTagsList}
        </div>
    );
}

export default observer(TagSelector);